import {
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, ReplaySubject } from 'rxjs';
import { CsvColumn } from 'src/app/models/csv-export.model';
import { PartType } from 'src/app/models/maintenancelogs.model';
import {
  PartCounterCsvRow,
  PartId,
  PartWithCounterDetailsForTable,
} from 'src/app/models/parts-info.model';
import { TitleCaseReplacePipe } from 'src/app/pipes/title-case-replace.pipe';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PartListService } from 'src/app/services/part-list.service';
import { getDeviceDisplayName } from 'src/app/utils/device-display';
import { formatPartSubType, formatPartType } from 'src/app/utils/part-display';
import { buildTimestamp } from 'src/utils/general-utils';
import { ChipOption } from '../chip-selector/chip-selector.component';
import {
  ColumnDefinition,
  ColumnFilterConfig,
  DataColumnDefinition,
} from '../qs-table/column-definition.interface';

@Component({
  selector: 'app-part-list-table',
  standalone: false,
  templateUrl: './part-list-table.component.html',
  styleUrl: './part-list-table.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PartListTableComponent implements OnInit {
  partsList: PartWithCounterDetailsForTable[] = [];
  partCounterCsvRow: PartCounterCsvRow[] = [];
  private partListService = inject(PartListService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private loadingService = inject(LoadingService);
  private titleCaseReplacePipe = new TitleCaseReplacePipe();
  private injector = inject(Injector);
  private router = inject(Router);

  filterOptions: ChipOption[] = [];

  columns: ColumnDefinition<PartWithCounterDetailsForTable>[] = [
    {
      type: 'data',
      field: 'partId.partType',
      header: this.translateService.instant('partList.table_header.partType'),
      format: (row) => this.formatPartType(row!),
      sortFn: (a, b, multiplier) => {
        return (
          a!.partId!.partType!.localeCompare(b!.partId!.partType!) * multiplier
        );
      },
      style: (row) => {
        return { 'font-weight': 'bold' };
      },
    } as DataColumnDefinition<PartWithCounterDetailsForTable>,

    {
      type: 'data',
      field: 'partId.partSubType',
      header: this.translateService.instant(
        'partList.table_header.part_subType',
      ),

      format: (row) => this.formatPartSubType(row!),
      sortFn: (a, b, multiplier) => {
        return (
          this.formatPartSubType(a!).localeCompare(this.formatPartSubType(b!)) *
          multiplier
        );
      },
    } as DataColumnDefinition<PartWithCounterDetailsForTable>,

    {
      type: 'data',
      field: 'partId.id',
      header: this.translateService.instant('partList.table_header.part_id'),
      format: (row) => row!.partId!.id!.toUpperCase(),
      sortFn: (a, b, multiplier) => {
        return a!.partId!.id!.localeCompare(b!.partId!.id!) * multiplier;
      },
    } as DataColumnDefinition<PartWithCounterDetailsForTable>,
    {
      type: 'data',
      field: 'lastConnectedDevice',
      header: this.translateService.instant(
        'partList.table_header.last_device',
      ),
      format: (row) => this.formatLastConnectedDevice(row) || '-',
      sortFn: (a, b, multiplier) => {
        return (
          this.formatLastConnectedDevice(a).localeCompare(
            this.formatLastConnectedDevice(b),
          ) * multiplier
        );
      },
      onClick: (row) => this.goToLastConnectedDevice(row),
      style: (row) =>
        row.lastConnectedDevice?.deviceId
          ? { cursor: 'pointer', 'text-decoration': 'underline' }
          : {},
    } as DataColumnDefinition<PartWithCounterDetailsForTable>,
    {
      type: 'data',
      field: 'counterToDisplay',
      header: this.translateService.instant(
        'partList.table_header.counter_alert',
      ),
      format: (row) => this.formatCounterAlert(row),
      sortFn: (
        a: PartWithCounterDetailsForTable,
        b: PartWithCounterDetailsForTable,
        multiplier,
      ) => {
        const valueA = a.counterToDisplay?.percentage ?? -1;
        const valueB = b.counterToDisplay?.percentage ?? -1;
        let comparator = 0;
        if (valueA < valueB) {
          comparator = -1;
        } else if (valueA > valueB) {
          comparator = 1;
        }
        return comparator * multiplier;
      },
      style: (row) => {
        if (
          row.counterToDisplay.percentage! >= 60 &&
          row.counterToDisplay.percentage! < 80
        )
          return { color: '#EE8B00', 'font-weight': 'bold' };
        if (row.counterToDisplay.percentage! >= 80)
          return { color: '#FC0000', 'font-weight': 'bold' };
        return {};
      },
    } as DataColumnDefinition<PartWithCounterDetailsForTable>,
  ];
  readonly PART_COUNTERS_CSV_COLUMNS: CsvColumn<PartCounterCsvRow>[] = [
    {
      header: this.translateService.instant('partList.table_header.partType'),
      accessor: 'partType',
    },
    {
      header: this.translateService.instant(
        'partList.table_header.part_subType',
      ),
      accessor: 'partSubType',
    },
    {
      header: this.translateService.instant('partList.table_header.part_id'),
      accessor: 'partId',
    },
    {
      header: this.translateService.instant(
        'partList.table_header.last_device',
      ),
      accessor: 'lastConnectedDevice',
    },
    {
      header: this.translateService.instant(
        'partList.table_header.counter_type',
      ),
      accessor: 'counterType',
    },
    {
      header: this.translateService.instant('partList.table_header.counter'),
      accessor: 'counterValue',
    },
    {
      header: this.translateService.instant('partList.table_header.threshold'),
      accessor: 'threshold',
    },
    {
      header: this.translateService.instant('partList.table_header.percentage'),
      accessor: 'percentage',
    },
  ];

  isSearchEnabled = true;

  columnFilterConfig: ColumnFilterConfig = {
    field: 'partType',
    filterOptions: this.generateFilterOptions(Object.values(PartType)),
    filterFunc: (rows, selectedChips) => {
      return rows.filter((row) =>
        selectedChips.includes(this.formatOptionValue(row.partId.partType!)),
      );
    },
  };

  ngOnInit() {
    this.loadPartsData().subscribe();
  }

  generateFilterOptions(options: string[]): ChipOption[] {
    let chiOptions: ChipOption[] = [];
    options.forEach((option) => {
      chiOptions.push({
        displayName: this.translateService.instant(
          'drone.maintenance.part_type.' + option,
        ),
        value: this.formatOptionValue(option),
        isSelected: true,
      });
    });
    return chiOptions;
  }
  formatOptionValue(text: string): string {
    return text.replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
  }

  formatPartType(row: PartWithCounterDetailsForTable): string {
    return formatPartType(
      row.partId?.partType ?? PartId.PartTypeEnum.Other,
      row.qsUavPartType,
    );
  }

  /**
   * Sub types arrive as raw CBDA values, e.g. 'HW_SUB_TYPE_WING_R24_VECTOR'.
   */
  formatPartSubType(row: PartWithCounterDetailsForTable): string {
    return formatPartSubType(row.partId?.partSubType) || '-';
  }

  formatCounterValue(value?: string, unit?: string): string {
    if (!value) {
      return '';
    }

    const displayUnit = unit?.trim();
    return displayUnit && displayUnit !== 'unknown'
      ? `${value} ${displayUnit}`
      : value;
  }

  formatCounterAlert(row?: PartWithCounterDetailsForTable): string {
    if (!row?.counterToDisplay?.type) {
      return '-';
    }

    return (
      row.counterToDisplay.percentage +
      '% - ' +
      this.titleCaseReplacePipe.transform(row.counterToDisplay.type)
    );
  }

  formatLastConnectedDevice(row?: PartWithCounterDetailsForTable): string {
    return getDeviceDisplayName(row?.lastConnectedDevice);
  }

  goToLastConnectedDevice(row: PartWithCounterDetailsForTable): void {
    const deviceId = row.lastConnectedDevice?.deviceId?.trim();
    if (!deviceId) {
      return;
    }

    this.router.navigate(['/device', deviceId], {
      queryParams: { returnTo: '/resources' },
    });
  }

  hasCounters(row: PartWithCounterDetailsForTable): boolean {
    return !!row.counters?.length;
  }

  loadPartsData(): Observable<void> {
    this.loadingService.loading();
    const replySubject = new ReplaySubject<void>();

    this.partListService
      .getPartsListForTable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((parts) => {
        this.partsList = parts;
        this.partCounterCsvRow = this.toCounterRows(parts);
        replySubject.next();
        replySubject.complete();
        this.loadingService.loaded();
      });
    return replySubject.asObservable();
  }

  private toCounterRows(
    parts: PartWithCounterDetailsForTable[],
  ): PartCounterCsvRow[] {
    return (parts ?? []).flatMap((p) => {
      const base = {
        partType: this.formatPartType(p),
        partSubType: formatPartSubType(p.partId?.partSubType),
        partId: (p.partId?.id ?? '').toUpperCase(),
        lastConnectedDevice: this.formatLastConnectedDevice(p),
      };

      const counters = p.counters ?? [];
      if (!counters.length) {
        return [
          {
            ...base,
            counterType: '',
            counterValue: '',
            threshold: '',
            percentage: '',
          },
        ];
      }

      return counters.map((c) => ({
        ...base,
        counterType: this.titleCaseReplacePipe.transform(c.type ?? ''),
        counterValue: this.formatCounterValue(c.value, c.unit ?? c.valueType),
        threshold: this.formatCounterValue(c.threshold, c.unit ?? c.valueType),
        percentage: c.percentage != null ? String(c.percentage) : '',
      }));
    });
  }

  csvExportParts(): void {
    const csvService = this.injector.get(CsvExportService);

    csvService.exportToCsv(
      this.partCounterCsvRow.length ? this.partCounterCsvRow : [],
      this.PART_COUNTERS_CSV_COLUMNS,
      {
        fileName: `parts_${buildTimestamp()}.csv`,
      },
    );
  }
}
